/*
 * Tipos do wire da conciliacao bancaria assistida (FE-3 CP-A — ADR 0026).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend em `master`
 * (`flow/api/conciliacao/rotas.py`). Regra de projeto INVIOLAVEL: TODO valor
 * monetario e `string` aqui — nunca `number`. O wire entrega `NUMERIC(18,2)`
 * serializado como string decimal (ex.: `"1234567.89"`); converte-lo para
 * `number` em JS perderia precisao (IEEE-754 nao representa todo decimal exato e
 * estoura acima de `Number.MAX_SAFE_INTEGER`). O formatador em `../formato/decimal`
 * opera sobre a STRING, sem `Number`/`parseFloat`.
 */

/**
 * Empresa conciliavel pelo escopo do usuario
 * (`GET /api/conciliacao/empresas`).
 *
 * `fantasia` e anulavel (nem toda empresa cadastra nome fantasia). A lista pode
 * vir VAZIA (`[]`) quando o escopo do usuario nao inclui empresa alguma — o tipo
 * ja contempla; o tratamento de "escopo vazio" na UI e do CP-B.
 */
export interface EmpresaConciliavel {
  id: string;
  razao_social: string;
  fantasia: string | null;
  cnpj: string;
}

/**
 * Resumo da transacao bancaria associada ao item de fila.
 *
 * CONTRATO LIMPO do backend (decisao B): `cnpj_masc` da TRANSACAO vem mascarado
 * (3 primeiros + ***) ou `null` — NUNCA o CNPJ em claro. `valor` e STRING
 * decimal (ver nota de precisao no topo). `data` e ISO (`YYYY-MM-DD`).
 */
export interface TransacaoResumo {
  valor: string;
  data: string;
  tipo: string;
  cnpj_masc: string | null;
  descricao: string | null;
}

/**
 * Candidato de casamento que o motor listou para o item de fila.
 *
 * `score`/`valor` sao STRINGS do JSONB (ou `null` para `score`). `cnpj_masc` aqui
 * e SEMPRE `string` (nao anulavel como o da transacao). ATENCAO: o backend pode
 * entregar a string literal `"None"` neste campo (follow-up conhecido) — o
 * tratamento defensivo `"None"` -> vazio NAO e desta camada de dados, e do CP-B
 * (UI). Aqui o tipo permanece `string`. `data_prevista` e ISO (`YYYY-MM-DD`).
 */
export interface Candidato {
  id: string;
  score: string | null;
  cnpj_masc: string;
  valor: string;
  data_prevista: string;
}

/** Status da fila de conciliacao (filtro do `GET .../fila?status=`). */
export type FilaStatus = "aberta" | "resolvida";

/**
 * Motivo pelo qual o item caiu na fila manual (Literal do backend).
 *
 * A chave tecnica NUNCA aparece ao operador — a UI usa apenas o rotulo de
 * `LABELS_MOTIVO`.
 */
export type MotivoFila =
  | "L2_SUGESTAO"
  | "valor_data_unico"
  | "score_baixo"
  | "sem_candidato";

/**
 * Catalogo de rotulos PT-BR dos motivos (decisao a).
 *
 * O `Record<MotivoFila, string>` torna o catalogo EXAUSTIVO: se o backend
 * introduzir um motivo novo no `MotivoFila`, o compilador acusa a falta do rotulo
 * aqui (em vez de a UI exibir a chave tecnica crua ao operador). Fonte UNICA dos
 * rotulos — a UI nunca constroi texto de motivo por conta propria.
 */
export const LABELS_MOTIVO: Record<MotivoFila, string> = {
  L2_SUGESTAO: "Sugestão automática",
  valor_data_unico: "Valor e data conferem",
  score_baixo: "Baixa confiança",
  sem_candidato: "Sem candidato",
};

/**
 * Item da fila de conciliacao
 * (`GET /api/empresas/{empresaId}/conciliacao/fila?status=`).
 *
 * `candidatos` pode vir vazio (motivo `sem_candidato`). `criada_em` e ISO
 * datetime. `transacao` enriquece a fila com o contexto que o operador julga.
 */
export interface ItemFilaConciliacao {
  id: string;
  transacao_id: string;
  motivo: MotivoFila;
  status: FilaStatus;
  candidatos: Candidato[];
  criada_em: string;
  transacao: TransacaoResumo;
}

/**
 * Movimento resultante da confirmacao manual
 * (resposta do `POST .../fila/{filaId}/confirmar`).
 *
 * SEM PII (sem CNPJ/valor). `obrigacao_id` XOR `receita_id`: o titulo casado e de
 * uma das duas naturezas, a outra vem `null`.
 */
export interface MovimentoConciliacao {
  id: string;
  transacao_id: string;
  obrigacao_id: string | null;
  receita_id: string | null;
  tipo: string;
  status: string;
}

/**
 * Corpo do `POST .../fila/{filaId}/confirmar`: o humano escolhe UM candidato.
 *
 * `candidato_id` deve estar ENTRE os candidatos que o motor listou na fila — o
 * backend valida pertinencia (422 caso contrario).
 */
export interface ConfirmarConciliacaoIn {
  candidato_id: string;
}

/*
 * --- FE-4: Desfazer conciliacao (Tela 4 §10.5) ---------------------------------
 *
 * Leitura dos movimentos JA conciliados + acao de DESFAZER. Espelha o gapB do
 * backend (`GET .../conciliacao/movimentos`) e o `POST .../desfazer` existente.
 */

/**
 * Resumo do TITULO casado no movimento (lado obrigacao/receita).
 *
 * `valor`/`data_prevista` sao STRINGS (ver nota de precisao no topo). `cnpj_masc`
 * ja vem MASCARADO do backend (ou `null`). `lado` e o wire lowercase
 * (`obrigacao`/`receita`) — o rotulo PT-BR com acento e EXIBICAO (`LADO_MAP`).
 */
export interface TituloResumo {
  lado: string;
  valor: string;
  data_prevista: string;
  cnpj_masc: string | null;
}

/**
 * Movimento de conciliacao JA realizado
 * (`GET /api/empresas/{empresaId}/conciliacao/movimentos?status=ativa`).
 *
 * `score` e STRING (`"0.950"`) ou `null` — NUNCA `number`. `criado_em` e ISO
 * datetime. `tipo` e o wire lowercase (`automatico`/`manual`) — rotulo PT-BR em
 * `TIPO_MAP`. Reusa `TransacaoResumo` (mesma forma do item de fila).
 */
export interface MovimentoConciliado {
  id: string;
  tipo: string;
  score: string | null;
  status: string;
  criado_em: string;
  transacao: TransacaoResumo;
  titulo: TituloResumo;
}

/**
 * Corpo do `POST .../movimentos/{movimentoId}/desfazer`.
 *
 * `justificativa` exige >= `LIMITE_DESFAZER` chars apos `.trim()` — mesmo gate do
 * backend (422 abaixo disso). O efeito server-side reverte o movimento e recalcula
 * a projecao (resposta `MovimentoConciliacao` com status "desfeita").
 */
export interface DesfazerConciliacaoIn {
  justificativa: string;
}

/** Minimo de caracteres da justificativa de desfazer (apos `.trim()`). */
export const LIMITE_DESFAZER = 10;

/**
 * Rotulos PT-BR do TIPO do movimento (wire `automatico`/`manual`). Fonte UNICA —
 * a UI nunca constroi o texto por conta propria. Objeto `as const` consultado com
 * fallback seguro (`?? valor cru`) na UI, para tolerar um tipo novo do backend.
 */
export const TIPO_MAP = {
  automatico: "Automática",
  manual: "Manual",
} as const;

/** Rotulos PT-BR do TIPO da transacao (wire `debito`/`credito`). */
export const TX_TIPO_MAP = {
  debito: "Débito",
  credito: "Crédito",
} as const;

/** Rotulos PT-BR do LADO do titulo (wire `obrigacao`/`receita`). */
export const LADO_MAP = {
  obrigacao: "Obrigação",
  receita: "Receita",
} as const;
